import { KeyNames } from "@regrello/core-utils";
import { useRef } from "react";
import { useMount } from "react-use";

export namespace useDataGridV2FirstKeystroke {
  export type Args<T> = {
    /** The `eventKey` from the RegrelloDataGrid event. */
    eventKey: string | null;

    /** Callback to invoke to change the value of the cell. */
    onValueChange: (value: T) => void;

    /** Callback to invoke to convert the `eventKey` into the appropriate type for this input. */
    parseEventKey: (eventKey: string) => T;

    /**
     * Whether to suppress writing null values to the `onValueChange` callback.
     *
     * This is useful when invalid keystrokes are to be ignored in favor of preserving the existing
     * value of the cell. When true, this blocks calls to `onValueChange` when the `parseEventKey`
     * returns null, as writing null indicates that the value should be cleared.
     *
     * @default false
     */
    suppressWritingNull?: boolean;
  };

  export type Return = {
    /** A ref that should be passed to the input element. */
    inputRef: React.RefObject<HTMLInputElement | null>;
  };
}

/**
 * Hook that replaces the current value if the user pressed a printable key to enter edit mode.
 * This runs logic only on mount, so no need to memoize anything.
 */
export function useDataGridV2FirstKeystroke<T>({
  eventKey,
  onValueChange,
  parseEventKey,
  suppressWritingNull: isWritingNullSuppressed = false,
}: useDataGridV2FirstKeystroke.Args<T>): useDataGridV2FirstKeystroke.Return {
  const inputRef = useRef<HTMLInputElement>(null);

  // Describes the various behaviors for when the user enters edit mode via a key press.
  useMount(() => {
    if (eventKey === KeyNames.ENTER) {
      const currentInput = inputRef.current;
      if (currentInput == null) {
        return;
      }
      currentInput.focus();
      currentInput.select();
    } else if (eventKey != null) {
      const value = parseEventKey(eventKey);
      if (isWritingNullSuppressed && value == null) {
        return;
      }
      onValueChange(value);
    }
  });

  return { inputRef };
}
